import {Breadcrumbs} from 'components/Breadcrumbs/Breadcrumbs';
import {UserRole} from 'enums';
import React, {FunctionComponent, useState} from 'react';
import {NavLink, useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {UsersService, UpdatesService} from 'services';

import './Sidebar.scss';
import { formatDateDay } from 'services/Utils';
import { useLuxonTimezone } from 'hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { SplitButton } from 'components';
import { Dropdown } from 'react-bootstrap';
import { TemplatesModal } from './TemplatesModal';

interface ISidebarProps {
    className?: string,
}

export interface ISidebarRouteState {
    returnURL?: string,
}

export const Sidebar: FunctionComponent<ISidebarProps> = ({className}) => {
    const history = useHistory();
    const location = useLocation<ISidebarRouteState>();

    let label = "";
    let returnUrl = "";
    // Returns a route if we're on the new/edit update page
    const matchUpdate = useRouteMatch({
        path: '/updates/:id',
        exact: true,
    })
    // Returns a route if we're on the new update files page
    const matchUpdateFile = useRouteMatch({
        path: '/updateFiles/new',
        exact: true,
    })

    // Determines the label and return url for side menu
    if (matchUpdate) {
        label = 'Updates';
        returnUrl = '/';
    } else if (matchUpdateFile) {
        label = 'Update Files';
        returnUrl = '/updatefiles'
    }

    // If we were passed a return URL, use it
    if (location.state?.returnURL) {
        returnUrl = location.state.returnURL;
    }

    function newUpdate() {
        let url = "/updates/new"

        if (UpdatesService.newUpdateFirstStageStartDate) {
            const {timezone} = useLuxonTimezone(UsersService.me.timezone);
            const dateString = formatDateDay(UpdatesService.newUpdateFirstStageStartDate, timezone)
            url = url + "?startDate="+dateString
        }

        history.push(url);
    }

    const [showTemplatesModal, setShowTemplatesModal] = useState(false);

    function newUpdateFromTemplate(id, locations) {
        let url = "/updates/new"

        if (UpdatesService.newUpdateFirstStageStartDate) {
            const {timezone} = useLuxonTimezone(UsersService.me.timezone);
            const dateString = formatDateDay(UpdatesService.newUpdateFirstStageStartDate, timezone)
            url += `?startDate=${dateString}&templateID=${id}&locations=${locations.join(",")}`;
        } else url += `?templateID=${id}&locations=${locations.join(",")}`;

        history.push(url);
    }

    return (<>
        <div className={className}>
            {matchUpdate || matchUpdateFile ? (
                // If we're editing an update or creating an update file, show breadcrumbs instead of the nav.
                <Breadcrumbs crumbs={[{label: label, url: returnUrl}]} />
            ) : (<>
                {UsersService.oktaUserInfo.role >= UserRole.LimitedUser && (<>
                    <div className = "sidebar_newUpdateWrap">
                        <SplitButton size="small" {...{
                            theme: "redOutline",
                            title: "New Update", 
                            onClick: newUpdate,
                            className: "sidebar_newUpdateButton"
                        }}>
                            <Dropdown.Item
                                onClick={() => setShowTemplatesModal(true)}
                            >
                                From Template
                            </Dropdown.Item>
                        </SplitButton>
                    </div >
                </>)}
                <div className="sidebar_nav">
                    <NavLink to="/" className="sidebar_navLink" activeClassName="active" exact>
                        Updates
                    </NavLink>
                    <NavLink to="/reports" className="sidebar_navLink" activeClassName="active" exact>
                        Reports
                    </NavLink>
                    {UsersService.oktaUserInfo.role >= UserRole.LimitedUser && (
                        <NavLink to="/payload" className="sidebar_navLink" activeClassName="active" exact>
                            Payload Generator
                        </NavLink>
                    )}
                    {UsersService.oktaUserInfo.role >= UserRole.FullUser && (<>
                        <NavLink to="/locationGroups" className="sidebar_navLink" activeClassName="active" exact>
                            Location Groups
                        </NavLink>
                        <NavLink to="/updatefiles" className="sidebar_navLink" activeClassName="active" exact>
                            Update Files
                        </NavLink>
                    </>)}
                    {UsersService.oktaUserInfo.role >= UserRole.Admin && (<>
                        <NavLink to="/admin" className="sidebar_navLink" activeClassName="active" exact>
                            Admin
                        </NavLink>
                        <NavLink to="/manufacturers" className="sidebar_navLink" activeClassName="active" exact>
                            - Manufacturers
                        </NavLink>
                        <NavLink to="/models" className="sidebar_navLink" activeClassName="active" exact>
                            - Models
                        </NavLink>
                        <NavLink to="/updatetypes" className="sidebar_navLink" activeClassName="active" exact>
                            - Update Types
                        </NavLink>
                        <NavLink to="/approvers" className="sidebar_navLink" activeClassName="active" exact>
                            - Approvers
                        </NavLink>
                        <NavLink to="/templates" className="sidebar_navLink" activeClassName="active" exact>
                            - Templates
                        </NavLink>
                    </>)}
                    <NavLink className="sidebar_navLink" activeClassName="inactive" target='_blank' to={{pathname: process.env.ERC_SUPPORT_PORTAL_URL}}>
                        ERC Support <FontAwesomeIcon icon={faExternalLinkAlt}></FontAwesomeIcon>
                    </NavLink>
                    <NavLink className="sidebar_navLink" activeClassName="inactive" target='_blank' to={{pathname: process.env.PINGER_URL}}>
                        Pinger <FontAwesomeIcon icon={faExternalLinkAlt}></FontAwesomeIcon>
                    </NavLink>
                </div>
            </>)}

        </div>
        <TemplatesModal
            key={0} 
            show={showTemplatesModal}
            onCancel={() => {
                setShowTemplatesModal(false);
            }}
            onSubmit={(id, locations) => {
                setShowTemplatesModal(false);
                newUpdateFromTemplate(id, locations);
            }}
        />
    </>)
};
